/**
 * Hook to fetch Cognito user attributes.
 * 
 * This is used to determine if a user is a social login (has identities attribute)
 * which affects whether they can change their email/password.
 * 
 * Mirrors the mobile app's useUserAttributes hook from:
 * src/hooks/cognito/useUserAttributes.ts
 */

import { useQuery } from '@tanstack/react-query';
import { fetchUserAttributes, FetchUserAttributesOutput } from 'aws-amplify/auth';

export const userAttributesKeys = {
  all: ['userAttributes'] as const,
};

/**
 * Hook to fetch the current user's Cognito attributes.
 * 
 * The `identities` attribute indicates if the user signed up via a social provider
 * (Google, Facebook, Apple, etc.). If present, the user cannot change their email/password.
 * 
 * @returns Query result with user attributes
 */
export function useUserAttributes() {
  return useQuery({
    queryKey: userAttributesKeys.all,
    queryFn: async (): Promise<FetchUserAttributesOutput> => {
      return fetchUserAttributes();
    },
  });
}

/**
 * Check if the user is a social login (cannot change email/password).
 * 
 * @param userAttributes - The user attributes from useUserAttributes
 * @returns true if the user signed up via social provider
 */
export function isSocialLogin(userAttributes: FetchUserAttributesOutput | undefined): boolean {
  return !!userAttributes?.identities;
}

export default useUserAttributes;
